import { computed, ref, unref, watch, type MaybeRef } from 'vue'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import { defaultFetchLov } from './default-fetch'
import { loadLovBatch } from './load-batch'
import {
  buildLovCacheKey,
  clearPendingLov,
  getLovFromCache,
  getPendingLov,
  setLovCache,
  setPendingLov,
} from './lov-store'

import type {
  LovConfigContext,
  LovFetchFn,
  LovOption,
  LovTagType,
} from './types'

const DEFAULT_CACHE_TTL = 5 * 60 * 1000

export type {
  LovBatchFetchFn,
  LovConfigContext,
  LovFetchFn,
  LovOption,
  LovTagType,
}
export { loadLovBatch } from './load-batch'
export type { LoadLovBatchOptions } from './load-batch'
export { defaultFetchLovList, resolveFetchLovList } from './fetch-lov-list'
export { DEFAULT_LOV_LIST_URL } from './default-fetch'

export interface UseLovOptions {
  params?: MaybeRef<Record<string, unknown> | undefined>
  /** 列级静态选项，不调接口 */
  staticOptions?: MaybeRef<LovOption[] | undefined>
  immediate?: boolean
}

export function useLov(
  lovType: MaybeRef<string | undefined>,
  options: UseLovOptions = {}
) {
  const lovConfig = useGlobalConfig('lov')

  const typeRef = computed(() => unref(lovType) || '')
  const paramsRef = computed(() => unref(options.params))
  const staticRef = computed(() => unref(options.staticOptions))

  const optionsList = ref<LovOption[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)

  const resolveFetch = (): LovFetchFn => {
    return lovConfig.value?.fetchLov ?? defaultFetchLov
  }

  const resolveTtl = (): number => {
    const ttl = lovConfig.value?.cacheTtl
    return ttl === undefined ? DEFAULT_CACHE_TTL : ttl
  }

  const findOption = (value: unknown): LovOption | undefined => {
    if (value == null || value === '') return undefined
    const key = String(value)
    return optionsList.value.find((o) => o.value === key)
  }

  const getLabel = (value: unknown): string => {
    const found = findOption(value)
    if (found) return found.label
    if (value == null || value === '') return ''
    return String(value)
  }

  const getTagType = (value: unknown): LovTagType | undefined => {
    return findOption(value)?.tagType
  }

  const load = async () => {
    const staticOpts = staticRef.value
    if (staticOpts?.length) {
      optionsList.value = staticOpts
      loading.value = false
      error.value = null
      return
    }

    const type = typeRef.value
    if (!type) {
      optionsList.value = []
      return
    }

    const cacheKey = buildLovCacheKey(type, paramsRef.value)
    const ttl = resolveTtl()
    const cached = getLovFromCache(cacheKey, ttl)
    if (cached) {
      optionsList.value = cached
      loading.value = false
      error.value = null
      return
    }

    const inFlight = getPendingLov(cacheKey)
    loading.value = true
    error.value = null

    try {
      if (!inFlight && !paramsRef.value) {
        await loadLovBatch([type], {
          fetchBatch: lovConfig.value?.fetchLovBatch,
          fetchLov: resolveFetch(),
          cacheTtl: ttl,
        })
        const warmed = getLovFromCache(cacheKey, ttl)
        if (warmed) {
          optionsList.value = warmed
          return
        }
      }

      const promise =
        inFlight ??
        (() => {
          const p = resolveFetch()(type, paramsRef.value)
          setPendingLov(cacheKey, p)
          return p
        })()

      const data = await promise
      optionsList.value = data
      setLovCache(cacheKey, data, ttl)
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
      optionsList.value = []
    } finally {
      loading.value = false
      clearPendingLov(cacheKey)
    }
  }

  watch(
    [typeRef, paramsRef, staticRef],
    () => {
      void load()
    },
    { immediate: options.immediate !== false }
  )

  return {
    options: optionsList,
    loading,
    error,
    refresh: load,
    getLabel,
    getTagType,
    findOption,
  }
}
